import React, { useState, useRef } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { PrimaryButton } from '../ui/PrimaryButton';
import { GhostButton } from '../ui/GhostButton';
import { useToast } from '../../context/ToastContext';
import { UploadCloud, X, Loader } from 'lucide-react';
import { BASE_UPLOAD_URL } from '../../config';

export const CreateComponentModal = ({ isOpen, onClose, onCreate }) => {
    const { toast } = useToast();
    const { statuses } = usePIM(); // Get dynamic statuses

    const [name, setName] = useState('');
    const [sku, setSku] = useState('');
    const [role, setRole] = useState('component_internal');
    const [status, setStatus] = useState('In Production');
    const [category, setCategory] = useState('Component');
    const [imageUrl, setImageUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const fileInputRef = useRef(null);

    const handleSubmit = () => {
        if (!name) {
            toast.error("Name is required");
            return;
        }
        onCreate({ name, sku, role, status, category, image_url: imageUrl });
        handleClose();
    };

    const handleClose = () => {
        setName('');
        setSku('');
        setRole('component_internal');
        setStatus('In Production');
        setCategory('Component');
        setImageUrl(null);
        setIsUploading(false);
        onClose();
    };

    // --- Image Upload Logic ---
    const handleFileSelect = async (file) => {
        if (!file) return;

        // Basic validation
        if (!file.type.startsWith('image/')) {
            toast.error("Please upload an image file");
            return;
        }

        setIsUploading(true);
        const formData = new FormData();
        formData.append('image', file);

        try {
            const res = await fetch('/api/upload', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData
            });

            if (!res.ok) throw new Error("Upload failed");

            const data = await res.json();
            setImageUrl(data.url); // Store the returned URL
            toast.success("Image uploaded");
        } catch (err) {
            console.error("Upload error:", err);
            toast.error("Failed to upload image");
        } finally {
            setIsUploading(false);
        }
    };

    const onDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Create New Component" maxWidth="max-w-md">
            <div className="space-y-4">
                {/* Image Upload Area */}
                <div
                    className={`
                        relative border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center text-center transition-colors h-32
                        ${isDragging ? 'border-blue-500 bg-blue-900/20' : 'border-stone-700 hover:border-stone-600 bg-stone-900/30'}
                        ${imageUrl ? 'border-transparent p-0 overflow-hidden' : ''}
                    `}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    onClick={() => !imageUrl && fileInputRef.current?.click()}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleFileSelect(e.target.files?.[0])}
                    />

                    {isUploading ? (
                        <div className="flex flex-col items-center text-stone-400">
                            <Loader className="animate-spin mb-2" size={24} />
                            <span className="text-xs">Uploading...</span>
                        </div>
                    ) : imageUrl ? (
                        <div className="relative w-full h-full group">
                            <img src={`${BASE_UPLOAD_URL}/${imageUrl.replace(/^\/+/, '')}`} alt="Preview" className="w-full h-full object-contain" />
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <GhostButton onClick={(e) => { e.stopPropagation(); setImageUrl(null); }} className="text-white hover:text-red-400">
                                    <X size={20} /> Remove
                                </GhostButton>
                            </div>
                        </div>
                    ) : (
                        <div className="cursor-pointer text-stone-500 hover:text-stone-300">
                            <UploadCloud size={32} className="mx-auto mb-2" />
                            <p className="text-xs font-semibold">Click or Drag Image</p>
                        </div>
                    )}
                </div>

                <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase mb-1">Component Name *</label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Display Module 5.5 inch"
                        autoFocus
                    />
                </div>

                <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase mb-1">SKU (Optional)</label>
                    <Input
                        value={sku}
                        onChange={(e) => setSku(e.target.value)}
                        placeholder="Auto-generated if empty"
                    />
                    <p className="text-[10px] text-stone-500 mt-1">Leave empty to generate COMP-XXXXXX</p>
                </div>

                <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase mb-1">Role</label>
                    <select
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="w-full bg-stone-900 border border-stone-700 rounded px-3 py-2 text-sm text-stone-200 focus:outline-none focus:border-blue-500"
                    >
                        <option value="component_internal">Component (Internal)</option>
                        <option value="spare_part">Spare Part</option>
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase mb-1">Status</label>
                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="w-full bg-stone-900 border border-stone-700 rounded px-3 py-2 text-sm text-stone-200 focus:outline-none focus:border-blue-500"
                    >
                        {statuses && statuses.length > 0 ? (
                            statuses.map(s => (
                                <option key={s.id} value={s.name}>{s.name}</option>
                            ))
                        ) : (
                            <>
                                <option value="In Production">In Production</option>
                                <option value="End of Life">End of Life</option>
                            </>
                        )}
                    </select>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-stone-700">
                    <GhostButton onClick={handleClose}>Cancel</GhostButton>
                    <PrimaryButton onClick={handleSubmit} disabled={isUploading}>Create Component</PrimaryButton>
                </div>
            </div>
        </Modal>
    );
};
